import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  Brain,
  ShieldAlert,
  Clock,
  ArrowRight,
  PhoneCall,
  CheckCircle2,
  HelpCircle,
  TrendingUp,
} from "lucide-react";

export default function HowEffectiveIsSpravatoPage() {
  const publishedDate = "2024-01-30";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/01/how-effective-is-spravato.jpg";

  const faqs = [
    {
      q: "What is the success rate of SPRAVATO® esketamine nasal spray?",
      a: "Clinical trials demonstrate that approximately 70% of patients with treatment-resistant depression experience significant symptom reduction or remission when adding SPRAVATO® to an oral antidepressant.",
    },
    {
      q: "How fast does SPRAVATO® start working?",
      a: "Unlike traditional oral antidepressants that take 4 to 6 weeks, many SPRAVATO® patients report noticeable depressive relief within 24 to 48 hours following their initial doses.",
    },
    {
      q: "Is SPRAVATO® covered by health insurance?",
      a: "Yes. SPRAVATO® is FDA-approved for Treatment-Resistant Depression (TRD) and Major Depressive Disorder with suicidal ideation, and is covered by Medicare and most commercial insurance plans (BCBS, Aetna, Cigna, UHC).",
    },
    {
      q: "Why is two hours of post-dose observation required?",
      a: "FDA REMS safety guidelines require patients to remain in a certified healthcare setting for 2 hours post-dose to monitor for transient blood pressure changes or mild sedation.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="How Effective is SPRAVATO®? Clinical Efficacy & Response Rates | Neuro Wellness"
        description="Learn about the clinical efficacy, rapid onset, and response rates of SPRAVATO® esketamine nasal spray for treatment-resistant depression in Coral Springs, FL."
        canonical="/how-effective-is-spravato"
        keywords="how effective is spravato, spravato success rate, spravato clinical trial results, esketamine nasal spray efficacy, spravato treatment Coral Springs"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/how-effective-is-spravato#webpage",
                name: "How Effective is SPRAVATO®? Clinical Efficacy & Response Rates",
                url: "https://neurowellnesstms.com/how-effective-is-spravato",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Detailed clinical overview of SPRAVATO® esketamine efficacy, clinical trial statistics, rapid onset mechanism, and insurance coverage requirements.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/how-effective-is-spravato#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <TrendingUp className="h-4 w-4" />
                    FDA-Approved Esketamine Efficacy
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated Jan 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  How Effective is{" "}
                  <span className="text-accent">SPRAVATO®?</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  For adults who haven't found relief from traditional oral
                  antidepressants, SPRAVATO® esketamine nasal spray offers
                  rapid, targeted relief by working on glutamate receptors to
                  rebuild brain connectivity.
                </p>

                <div className="mt-6 flex flex-wrap gap-4 text-xs sm:text-sm text-primary-foreground/80">
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <Brain className="h-4 w-4 text-accent" />
                    <span>~70% Clinical Response Rate</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1.5 rounded-md backdrop-blur-sm border border-white/10">
                    <ShieldAlert className="h-4 w-4 text-accent" />
                    <span>FDA-Certified REMS Center</span>
                  </div>
                </div>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white p-5 sm:p-6 shadow-2xl text-foreground">
                  <div className="mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-accent">
                      SPRAVATO® Assessment
                    </span>
                    <h2 className="text-xl font-bold text-foreground mt-1">
                      Check SPRAVATO® Eligibility
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Verify your insurance coverage and eligibility at our
                      Coral Springs, FL clinic.
                    </p>
                  </div>
                  <EligibilityCheckForm
                    source="how-effective-is-spravato"
                    variant="card"
                    redirectTo="/thank-you-consultation"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BREADCRUMBS */}
        <div className="container-px mx-auto max-w-4xl pt-6">
          <Breadcrumbs
            crumbs={[
              { name: "Resources", href: "/articles" },
              {
                name: "How Effective is SPRAVATO",
                href: "/how-effective-is-spravato",
              },
            ]}
          />
        </div>

        {/* MAIN ARTICLE BODY */}
        <article className="container-px mx-auto max-w-4xl py-6 space-y-12">
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Clinical Trial Findings & Response Rates
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              In pivotal FDA clinical trials, patients with Treatment-Resistant
              Depression (TRD) who added SPRAVATO® to an oral antidepressant
              demonstrated statistically significant improvement compared to
              those who took an oral antidepressant plus a placebo spray.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 pt-2">
              <div className="rounded-xl border p-4 bg-card text-center space-y-1">
                <div className="text-3xl font-extrabold text-accent">~70%</div>
                <div className="text-xs font-bold text-foreground">
                  Response Rate
                </div>
                <p className="text-xs text-muted-foreground">
                  Experienced 50%+ reduction in depressive symptoms
                </p>
              </div>
              <div className="rounded-xl border p-4 bg-card text-center space-y-1">
                <div className="text-3xl font-extrabold text-accent">
                  24 Hours
                </div>
                <div className="text-xs font-bold text-foreground">
                  Rapid Onset
                </div>
                <p className="text-xs text-muted-foreground">
                  Many report initial symptom relief within hours
                </p>
              </div>
              <div className="rounded-xl border p-4 bg-card text-center space-y-1">
                <div className="text-3xl font-extrabold text-accent">51%</div>
                <div className="text-xs font-bold text-foreground">
                  Relapse Reduction
                </div>
                <p className="text-xs text-muted-foreground">
                  Lower risk of depressive relapse during maintenance
                </p>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">
              Why SPRAVATO® Works Differently Than Oral Meds
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Most standard antidepressants target monoamines (serotonin,
              norepinephrine, dopamine). SPRAVATO® uniquely targets glutamate,
              the brain's most abundant neurotransmitter. By boosting
              synaptogenesis and synapse connectivity in prefrontal circuits,
              SPRAVATO® repairs neural pathways damaged by chronic stress and
              depression.
            </p>
          </section>

          {/* FAQ SECTION */}
          <section className="space-y-6 pt-6 border-t">
            <div className="flex items-center gap-2 text-accent">
              <HelpCircle className="h-6 w-6" />
              <h2 className="text-2xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-xl border p-5 bg-card">
                  <h3 className="text-base font-bold text-foreground mb-2">
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {faq.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* RELATED PAGES */}
          <section className="space-y-4 pt-6 border-t">
            <h3 className="text-xl font-bold text-foreground">
              Related Resources
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/spravato"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="font-bold text-sm text-foreground">
                    SPRAVATO® Treatment Hub
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Full overview of treatment protocols & suites
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
              </Link>
              <Link
                to="/spravato-vs-ketamine"
                className="p-4 rounded-xl border bg-card hover:border-accent transition-colors flex items-center justify-between"
              >
                <div>
                  <h4 className="font-bold text-sm text-foreground">
                    SPRAVATO® vs IV Ketamine
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    Comparing FDA approval, insurance, & administration
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-accent" />
              </Link>
            </div>
          </section>

          {/* CTA BANNER */}
          <section className="rounded-2xl bg-gradient-to-br from-primary-deep to-slate-900 p-6 sm:p-8 text-white space-y-4">
            <h3 className="text-2xl font-bold">
              See If SPRAVATO® Is Right For You
            </h3>
            <p className="text-sm text-primary-foreground/90 leading-relaxed">
              Our clinical team in Coral Springs, FL will verify your insurance
              and guide you through every step of SPRAVATO® treatment.
            </p>
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="tel:954-827-9793"
                className="inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-slate-950 hover:bg-accent/90 transition-colors"
              >
                <PhoneCall className="h-4 w-4" /> Call 954-827-9793
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/20 transition-colors"
              >
                Request Eligibility Check
              </Link>
            </div>
          </section>
        </article>
      </main>

      <Footer />
    </div>
  );
}
